






























































































import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import Vue, {PropType} from 'vue';
import ErrorInfo from '@/components/types/ErrorInfo';
import FileLine from '@/components/types/FileLine';
import FileList from '../components/FileList.vue';
import VueCodemirror from 'vue-codemirror/src/index';
import 'codemirror/addon/mode/simple';
import 'codemirror/addon/edit/matchbrackets';

export default Vue.extend({
    name: 'source-code',

    components: { 'code-mirror': codemirror, FileList },

    props: {
        code:           { type: [String, Array, Uint8Array] as PropType<FileContent>, required: true },
        filename:       { type: String, required: true },
        readOnly:       { type: Boolean, default: (): boolean => false },
        error:          { type: Object as PropType<ErrorInfo | undefined> },
        breakpoints:    { type: Array as PropType<number[]>, default: () : number[] => [] },
        filePopup:      { type: Boolean, default: () => false },
        filePopupFiles: { type: Array as PropType<string[]>, default: () => [] },
        highlightLine: Number,
    },

    data() : {
        cmOptions: any,
        errorLineWidget: any,
        lineClass: any,
    } {
        return {
            cmOptions: {
                tabSize: 8,
                lineNumbers: true,
                line: true,
                lineWiseCopyCut: false,
                autofocus: true,
                readOnly: this.readOnly || this.code instanceof Array || this.code instanceof Uint8Array,
                gutters: ['source-bkp', 'CodeMirror-linenumbers'],
                indentWithTabs: true,
                matchBrackets: true,
                // mode: 'text/javascript',
            },
            errorLineWidget: undefined,
            lineClass: undefined,
        };
    },

    computed: {
        processedCode() : string {
            if (this.code instanceof Array || this.code instanceof Uint8Array)
                return this.binaryString();
            else
                return this.code as string;
        },
    },

    methods: {
        instance() : any {
            return (this.$refs.codemirror as HTMLFormElement).codemirror;
        },

        scrollTo(line: number) {
            this.instance().scrollIntoView({ line, ch: 0 }, 30);
            // TODO - don't move to the last line
        },

        binaryString() : string {
            const txt = [];
            let alpha = [];
            for (let addr = 0; addr < (Math.round(this.code.length / 16) + 1) * 16; ++addr) {
                const data = this.code[addr] as number | undefined;
                if (addr % 16 === 0)
                    txt.push(`${addr.toString(16).toUpperCase().padStart(6, '0')}  `);
                if (data === undefined) {
                    txt.push('   ');
                    alpha.push(' ');
                } else {
                    txt.push(`${data.toString(16).toUpperCase().padStart(2, '0')} `);
                    alpha.push((data >= 32 && data < 127) ? String.fromCharCode(data) : '.');
                }
                if (addr % 16 === 7) {
                    txt.push(' ');
                    alpha.push(' ');
                }
                if (addr % 16 === 15) {
                    txt.push(`  ${alpha.join('')}\n`);
                    alpha = [];
                }
            }
            return `Binary data:\n\n${txt.join('')}`;
        },

        addErrorWidget(error: ErrorInfo) {
            const msg = document.createElement('div');
            const icon = msg.appendChild(document.createElement('span'));
            icon.innerHTML = '!';
            icon.className = 'error-icon';
            msg.appendChild(document.createTextNode(error.message));
            msg.className = 'error-message';
            this.errorLineWidget = this.instance().addLineWidget(error.line - 1, msg);
        },

        removeErrorWidget() {
            this.instance().removeLineWidget(this.errorLineWidget);
            this.errorLineWidget = undefined;
        },

        addStepHighlight(line: number) {
            if (line < 0)
                return;
            this.lineClass = this.instance().addLineClass(line, 'background', 'source-step');
            this.scrollTo(line);
        },

        removeStepHighlight() {
            this.instance().removeLineClass(this.lineClass, 'background', 'source-step');
        },

        addBreakpoints(bkps : number[]) {
            for (const bkp of bkps) {
                const marker = document.createElement('div');
                marker.style.color = '#822';
                marker.style.position = 'relative';
                marker.style.top = '-3px';
                marker.style.paddingLeft = '12px';
                marker.innerHTML = '&#x25cf;';
                this.instance().getDoc().setGutterMarker(bkp - 1, 'source-bkp', marker);
            }
        },

        removeBreakpoints() {
            this.instance().getDoc().clearGutter('source-bkp');
        },

        focus() {
            this.instance().focus();
        },
    },

    watch: {
        error(newError?: ErrorInfo, oldError?: ErrorInfo) {
            if (oldError)
                this.removeErrorWidget();
            if (newError)
                this.addErrorWidget(newError!);
        },

        highlightLine(newLine?: number, oldLine?: number) {
            if (oldLine !== undefined)
                this.removeStepHighlight();
            if (newLine !== undefined)
                this.addStepHighlight(newLine!);
        },

        breakpoints(newBkps: number[]) {
            this.removeBreakpoints();
            this.addBreakpoints(newBkps);
        },
    },

    mounted() {
        if (this.error)
            this.addErrorWidget(this.error);
        if (this.highlightLine !== undefined)
            this.addStepHighlight(this.highlightLine);
        this.instance().on('gutterClick', (cm: any, n: number) => this.$emit('swap-breakpoint', { filename: this.filename, line: n + 1 } as FileLine));
        this.addBreakpoints(this.breakpoints);

        // define source code highlighting
        (VueCodemirror as any).CodeMirror.defineSimpleMode('simplemode', {
            start: [
                { regex: /([^\w_]|^)(nop|dbg|mov|or|and|xor|shl|shr|not|add|sub|mul|div\$?|div|mod|inc|dec|ifne|ifeq|ifgt\$?|ifge\$?|ifle\$?|iflt\$?|push[bwa]|pop[bwan]|jmp|jsr|ret|dev|ivec|int|iret|wait|ienab)([\s\t]|$)/i, token: 'keyword' },
                { regex: /([^\w_]|^)(org|db|dw|bss)([\s\t]|$)/, token: 'builtin' },
                { regex: /"(?:[^\\]|\\.)*?(?:"|$)/, token: 'string' },
                { regex: /(0x[a-f\d]+|[-+]?(?:\.\d+|\d+\.?\d*)(?:e[-+]?\d+)?)/i, token: 'number' },
                { regex: /'.'/, token: 'number' },
                { regex: /;.*/, token: 'comment' },
                { regex: /\.?[a-z_][a-z0-9_]*:/i, token: 'def' },
            ],
            meta: {
                lineComment: ';',
            },
        });
        this.instance().setOption('mode', 'simplemode');

        this.instance().clearHistory();
    },

});
